import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from '../components/ui';
import ScrollRail from '../components/subs/ScrollRail';
import { submitSubApplication, getApplyAgreements, applyAgreementUrl } from '../api';
import { SECTIONS, FILES, REFERENCE_COUNT, FILE_MAX, FILE_TYPES, TRADES, AGREEMENT_KINDS, moneyOf, moneyText } from '../lib/subApplication';

/* workwith.e3es.com (Duncan 2026-10-27: "a URL for subcontractors who want to apply to work with us. that link should
   lead them to a questionnaire that they fill out"): the application on its own page -- no sign-in, nothing else of
   Summit. It lands in Subcontractors → Applications for the people given "Sub Applications". E3's Subcontractor
   Qualification Form's questions (lib/subApplication.js), one card per section; a phone answers it as easily. Left of the
   form, the pencil rail (Duncan 2026-10-28: "add that pencil slider thing to the left of the form also so I can see how
   far they are in the form"; ScrollRail.js, as on the punch list): a dot where each section starts, the one being
   answered marked, and how many of its required questions are answered -- a tick once they all are. Beside the form when
   the window has room for it, dots only when it's narrower, none on a phone. */

const STATES = ['TX', 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
// The agreements to read before answering, as uploaded on the Applications page (Duncan 2026-10-27).
const AGREEMENT_LABEL = Object.fromEntries(AGREEMENT_KINDS);
// E3's badge and name, at the top of the form and its thank-you.
const Brand = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 2px 2px' }}>
    <img src="/e3-logo.png" alt="" style={{ width: 44, height: 44, display: 'block' }} />
    <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-1)' }}>E3 Entegral Solutions</span>
  </div>
);
const MONEY_KEYS = SECTIONS.flatMap((s) => (s.fields || []).filter((f) => f.kind === 'money').map((f) => f.key));

const CSS = `
  .ap { min-height: 100vh; background: var(--bg-page); color: var(--text-1); font-family: var(--font-sans); padding: 28px 16px 72px; box-sizing: border-box; }
  .ap-wrap { max-width: 780px; margin: 0 auto; display: flex; flex-direction: column; gap: 14px; }
  .ap-body { display: grid; grid-template-columns: minmax(0, 780px); justify-content: center; gap: 24px; }
  .ap-body.with-rail { grid-template-columns: 140px minmax(0, 780px) 140px; }
  .ap-body.with-dots { grid-template-columns: 20px minmax(0, 780px); gap: 14px; }
  .ap-rail { position: sticky; top: 12px; height: calc(100vh - 24px); display: flex; }
  .ap-card { background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 14px; box-shadow: var(--shadow-sm); padding: 20px 22px; }
  .ap-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
  .ap-num { width: 26px; height: 26px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    font: 650 13px var(--font-sans); color: var(--accent-text); background: var(--accent-soft); }
  .ap-title { font-size: 17px; font-weight: 650; letter-spacing: -0.01em; }
  .ap-note { font-size: 13.5px; color: var(--text-3); margin: -8px 0 14px 36px; }
  .ap-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px 16px; }
  .ap-f { grid-column: span 2; min-width: 0; }
  .ap-f.wide { grid-column: 1 / -1; }
  .ap-f.short { grid-column: span 1; }
  .ap-label { display: block; font-size: 13.5px; font-weight: 600; color: var(--text-1); margin-bottom: 6px; }
  .ap-label i { color: var(--danger); font-style: normal; margin-left: 2px; }
  .ap-input { width: 100%; height: 42px; padding: 0 12px; border-radius: 9px; border: 1px solid var(--border-default); background: var(--bg-surface);
    color: var(--text-1); font: 400 15px var(--font-sans); box-sizing: border-box; outline: none; transition: border-color .15s, box-shadow .15s; }
  .ap-input::placeholder { color: var(--text-4); }
  .ap-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
  .ap-input.is-bad { border-color: var(--danger); }
  textarea.ap-input { height: 88px; padding: 10px 12px; resize: vertical; line-height: 1.45; }
  .ap-money { position: relative; }
  .ap-money > span { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 15px; pointer-events: none; }
  .ap-money > .ap-input { padding-left: 24px; }
  .ap-hint { font-size: 12.5px; color: var(--text-3); margin-top: 5px; line-height: 1.4; }
  .ap-err { font-size: 12.5px; color: var(--danger); margin-top: 5px; font-weight: 600; }
  .ap-pills { display: flex; gap: 8px; flex-wrap: wrap; }
  .ap-pill { height: 38px; padding: 0 18px; border-radius: 999px; border: 1px solid var(--border-default); background: var(--bg-surface); color: var(--text-2);
    font: 500 14.5px var(--font-sans); cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: background .15s, border-color .15s; }
  .ap-pill:hover { border-color: var(--border-strong); }
  .ap-pill[aria-checked="true"], .ap-pill[aria-pressed="true"] { background: var(--accent-soft); border-color: color-mix(in oklch, var(--accent) 50%, transparent); color: var(--text-1); font-weight: 600; }
  .ap-pill:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--accent-ring); }
  .ap-link { font-size: 13px; color: var(--accent-text); margin-top: 6px; display: inline-flex; align-items: center; gap: 5px; text-decoration: none; }
  .ap-refs { display: grid; grid-template-columns: 22px minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 0.9fr); gap: 10px; align-items: center; }
  .ap-drop { display: flex; align-items: center; gap: 12px; padding: 14px; border-radius: 12px; border: 1.5px dashed var(--border-strong); background: var(--bg-surface-2); }
  .ap-drop.has { border-style: solid; border-color: var(--border-default); background: var(--bg-surface); }
  .ap-btn { height: 42px; padding: 0 18px; border-radius: 10px; font: 600 15px var(--font-sans); cursor: pointer; display: inline-flex; align-items: center; gap: 8px; }
  .ap-btn.go { color: #fff; background: var(--accent); border: 1px solid transparent; }
  .ap-btn.go:disabled { opacity: .6; cursor: default; }
  .ap-btn.quiet { color: var(--text-2); background: var(--bg-surface); border: 1px solid var(--border-default); }
  .ap-hp { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
  @media (max-width: 640px) {
    .ap { padding: 16px 12px 56px; }
    .ap-card { padding: 16px; }
    .ap-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
    .ap-f { grid-column: 1 / -1; }
    .ap-f.short { grid-column: span 1; }
    .ap-note { margin-left: 0; }
    .ap-refs { grid-template-columns: minmax(0, 1fr); }
    .ap-refs > .ap-refn { display: none; }
  }
`;

const blankRefs = () => Array.from({ length: REFERENCE_COUNT }, () => ({ company: '', contact: '', phone: '' }));
const sizeText = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Whether a question has an answer. */
const answered = (form, f) => {
  const v = form[f.key];
  return f.kind === 'trades' ? !!(v && v.length) : v != null && String(v).trim() !== '';
};

/** What's wrong with the answers, by the question's key: what's required and still empty, an email, ZIP, phone or amount
    that doesn't read as one. */
function problemsOf(form, off) {
  const e = {};
  SECTIONS.forEach((s) => (s.fields || []).forEach((f) => {
    if (off.has(f.key)) return;
    const v = form[f.key];
    const has = answered(form, f);
    if (f.required && !has) { e[f.key] = f.kind === 'yesno' ? 'Pick Yes or No' : f.kind === 'trades' ? 'Pick at least one' : 'Required'; return; }
    if (!has || f.kind === 'trades') return;
    const t = String(v).trim();
    if (f.kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) e[f.key] = 'That doesn’t look like an email address';
    if (f.kind === 'zip' && !/^\d{5}(-\d{4})?$/.test(t)) e[f.key] = 'A 5-digit ZIP';
    if (f.kind === 'phone' && t.replace(/\D/g, '').length < 10) e[f.key] = 'A 10-digit phone number';
    if (f.kind === 'money' && moneyOf(t) == null) e[f.key] = 'An amount, like 2,000,000';
    if (f.kind === 'number' && !/^\d+(\.\d+)?$/.test(t.replace(/,/g, ''))) e[f.key] = 'A number';
  }));
  return e;
}

export default function ApplyPage() {
  const [form, setForm] = useState({ state: 'TX', trades: [], references: blankRefs() });
  const [files, setFiles] = useState({});
  const [fileErr, setFileErr] = useState({});
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(null);
  const [done, setDone] = useState(null);
  const hp = useRef(null); // a field only bots fill in
  const pickers = useRef({});
  // Which agreements are on file to read: { msa, subcontract } (null until read). A question about one that isn't
  // stays off the form.
  const [agreements, setAgreements] = useState(null);
  useEffect(() => { getApplyAgreements().then((a) => setAgreements(a || {})).catch(() => setAgreements({})); }, []);
  const off = new Set(SECTIONS.flatMap((s) => (s.fields || []).filter((f) => f.link && !(agreements && agreements[f.link])).map((f) => f.key)));
  const shownSections = SECTIONS.filter((s) => !s.fields || s.fields.some((f) => !off.has(f.key)));

  // The pencil rail: how far each section is answered (its required questions; references and documents by how many
  // are given), and where each starts down the page, measured as the page lays out and whenever it changes size.
  const progressOf = (s) => {
    const [n, of] = s.kind === 'references'
      ? [form.references.filter((r) => r.company.trim() || r.contact.trim() || r.phone.trim()).length, REFERENCE_COUNT]
      : s.kind === 'files'
        ? [FILES.filter((f) => files[f.key]).length, FILES.length]
        : [s.fields.filter((f) => f.required && !off.has(f.key) && answered(form, f)).length, s.fields.filter((f) => f.required && !off.has(f.key)).length];
    return n >= of ? '✓' : `${n}/${of}`;
  };
  const formRef = useRef(null);
  const [rail, setRail] = useState({ on: false, narrow: false, items: [] });
  const railKey = useRef(JSON.stringify(rail));
  const railSections = useRef([]);
  railSections.current = shownSections.map((s) => ({ id: s.key, label: s.title, count: progressOf(s) }));
  useLayoutEffect(() => {
    const measure = () => {
      const items = railSections.current.map((it) => {
        const el = document.getElementById(`ap-sec-${it.id}`);
        return el ? { ...it, top: Math.round(el.getBoundingClientRect().top + window.scrollY - 12) } : null;
      }).filter(Boolean);
      const w = window.innerWidth;
      const next = { on: w >= 700 && items.length > 0 && document.documentElement.scrollHeight > window.innerHeight + 40, narrow: w < 1140, items };
      const key = JSON.stringify(next);
      if (key === railKey.current) return;
      railKey.current = key;
      setRail(next);
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (ro && formRef.current) ro.observe(formRef.current);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  });

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };
  const setRef = (i, key, value) => setForm((f) => ({ ...f, references: f.references.map((r, k) => (k === i ? { ...r, [key]: value } : r)) }));
  // From the latest answers, so quick taps on two trades both count.
  const toggleTrade = (t) => {
    setForm((f) => ({ ...f, trades: f.trades.includes(t) ? f.trades.filter((x) => x !== t) : [...f.trades, t] }));
    if (errors.trades) setErrors((e) => { const n = { ...e }; delete n.trades; return n; });
  };
  const pick = (kind, file) => {
    if (!file) return;
    if (file.size > FILE_MAX) { setFileErr((e) => ({ ...e, [kind]: `${file.name} is ${sizeText(file.size)}: 10 MB at most` })); return; }
    setFileErr((e) => ({ ...e, [kind]: null }));
    setFiles((f) => ({ ...f, [kind]: file }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = problemsOf(form, off);
    setErrors(e);
    const first = Object.keys(e)[0];
    if (first) {
      const el = document.querySelector(`[data-field="${first}"]`);
      if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); const input = el.querySelector('input, textarea, select, button'); if (input) setTimeout(() => input.focus({ preventScroll: true }), 350); }
      return;
    }
    setSending(true);
    setFailed(null);
    try {
      const answers = { ...form, references: form.references.filter((r) => r.company.trim() || r.contact.trim() || r.phone.trim()) };
      MONEY_KEYS.forEach((k) => { if (answers[k] != null && String(answers[k]).trim()) answers[k] = moneyOf(answers[k]); });
      const r = await submitSubApplication({ answers, website_url: hp.current ? hp.current.value : '' }, files);
      setDone({ ...r, firm: form.firm_name, email: form.email });
      window.scrollTo(0, 0);
    } catch (err) {
      setFailed(err.message || 'It didn’t go through. Try again in a minute.');
    } finally {
      setSending(false);
    }
  };

  const field = (f) => {
    const v = form[f.key];
    const bad = !!errors[f.key];
    const label = <span className="ap-label">{f.label}{f.required && <i aria-hidden="true">*</i>}</span>;
    let control;
    if (f.kind === 'yesno') {
      control = (
        <div className="ap-pills" role="radiogroup" aria-label={f.label}>
          {[['yes', 'Yes'], ['no', 'No']].map(([val, text]) => (
            <button key={val} type="button" role="radio" aria-checked={v === val} className="ap-pill" onClick={() => set(f.key, val)}>{text}</button>
          ))}
        </div>
      );
    } else if (f.kind === 'trades') {
      control = (
        <div className="ap-pills" role="group" aria-label={f.label}>
          {TRADES.map((t) => (
            <button key={t} type="button" aria-pressed={form.trades.includes(t)} className="ap-pill" onClick={() => toggleTrade(t)}>
              {form.trades.includes(t) && <Icon name="check" size={13} stroke={2.4} />}{t}
            </button>
          ))}
        </div>
      );
    } else if (f.kind === 'state') {
      control = (
        <select className={`ap-input${bad ? ' is-bad' : ''}`} value={v || ''} onChange={(e) => set(f.key, e.target.value)} aria-label={f.label}>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    } else if (f.kind === 'text') {
      control = <textarea className={`ap-input${bad ? ' is-bad' : ''}`} value={v || ''} placeholder={f.placeholder} onChange={(e) => set(f.key, e.target.value)} aria-label={f.label} maxLength={2000} />;
    } else if (f.kind === 'money') {
      control = (
        <div className="ap-money">
          <span aria-hidden="true">$</span>
          <input className={`ap-input${bad ? ' is-bad' : ''}`} inputMode="numeric" value={v || ''} placeholder={f.placeholder || ''} aria-label={f.label}
            onChange={(e) => set(f.key, e.target.value.replace(/[^\d,.]/g, ''))}
            onBlur={() => { const n = moneyOf(v); if (n != null) set(f.key, moneyText(n).slice(1)); }} />
        </div>
      );
    } else {
      const type = f.kind === 'email' ? 'email' : f.kind === 'phone' ? 'tel' : 'text';
      const mode = f.kind === 'zip' || f.kind === 'number' ? 'numeric' : undefined;
      control = <input className={`ap-input${bad ? ' is-bad' : ''}`} type={type} inputMode={mode} value={v || ''} placeholder={f.placeholder || ''} aria-label={f.label}
        autoComplete={{ firm_name: 'organization', contact_name: 'name', address_line1: 'address-line1', city: 'address-level2', zip: 'postal-code', phone: 'tel', email: 'email', website: 'url' }[f.key]}
        onChange={(e) => set(f.key, e.target.value)} maxLength={f.kind === 'zip' ? 10 : 300} />;
    }
    const agreement = f.link && agreements && agreements[f.link];
    return (
      <div key={f.key} className={`ap-f${f.wide ? ' wide' : ''}${f.short ? ' short' : ''}`} data-field={f.key}>
        {label}
        {control}
        {agreement && (
          <a className="ap-link" href={applyAgreementUrl(f.link, agreement.uploaded_at)} target="_blank" rel="noopener noreferrer">
            <Icon name="external-link" size={13} />Read the {AGREEMENT_LABEL[f.link]}
          </a>
        )}
        {f.hint && !bad && <div className="ap-hint">{f.hint}</div>}
        {bad && <div className="ap-err" role="alert">{errors[f.key]}</div>}
      </div>
    );
  };

  if (done) {
    return (
      <div className="ap">
        <style>{CSS}</style>
        <div className="ap-wrap">
          <Brand />
          <div className="ap-card" style={{ textAlign: 'center', padding: '40px 24px' }}>
            <span style={{ width: 52, height: 52, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--success-soft)', color: 'var(--success)' }}>
              <Icon name="check" size={26} stroke={2.4} />
            </span>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 14, letterSpacing: '-0.015em' }}>Thanks, we’ve got it</div>
            <div style={{ fontSize: 15, color: 'var(--text-2)', marginTop: 8, lineHeight: 1.55, maxWidth: 480, marginInline: 'auto' }}>
              The application for {done.firm} is with E3’s construction team now. We’ll be in touch at {done.email}.
            </div>
            <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 14 }}>Application #{done.id}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ap">
      <style>{CSS}</style>
      <div className={`ap-body${rail.on ? (rail.narrow ? ' with-dots' : ' with-rail') : ''}`}>
        {rail.on && (
          <div className="ap-rail">
            <ScrollRail page box={null} items={rail.items} title="Sections" width={140} narrow={rail.narrow} />
          </div>
        )}
        <form ref={formRef} className="ap-wrap" onSubmit={submit} noValidate>
          <Brand />
          <div className="ap-card">
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>Work with E3</h1>
            <p style={{ margin: '8px 0 0', fontSize: 15, color: 'var(--text-2)', lineHeight: 1.55 }}>
              Tell us about your company to be considered as a subcontractor for E3 Entegral Solutions. It takes about 10 minutes;
              questions marked <span style={{ color: 'var(--danger)' }}>*</span> are required.
            </p>
          </div>
          {shownSections.map((s, i) => (
            <section key={s.key} id={`ap-sec-${s.key}`} className="ap-card" aria-labelledby={`ap-${s.key}`}>
              <div className="ap-head"><span className="ap-num">{i + 1}</span><span className="ap-title" id={`ap-${s.key}`}>{s.title}</span></div>
              {s.note && <div className="ap-note">{s.note}</div>}
              {s.fields && <div className="ap-grid">{s.fields.filter((f) => !off.has(f.key)).map(field)}</div>}
              {s.kind === 'references' && (
                <div className="ap-refs">
                  <span className="ap-refn" />
                  {['Company', 'Contact person', 'Phone'].map((h) => <span key={h} className="ap-refn" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)' }}>{h}</span>)}
                  {form.references.map((r, k) => (
                    <React.Fragment key={k}>
                      <span className="ap-refn mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{k + 1}</span>
                      <input className="ap-input" value={r.company} placeholder={`Reference ${k + 1}: company`} aria-label={`Reference ${k + 1} company`} onChange={(e) => setRef(k, 'company', e.target.value)} maxLength={200} />
                      <input className="ap-input" value={r.contact} placeholder="Contact person" aria-label={`Reference ${k + 1} contact person`} onChange={(e) => setRef(k, 'contact', e.target.value)} maxLength={200} />
                      <input className="ap-input" type="tel" value={r.phone} placeholder="Phone" aria-label={`Reference ${k + 1} phone`} onChange={(e) => setRef(k, 'phone', e.target.value)} maxLength={40} />
                    </React.Fragment>
                  ))}
                </div>
              )}
              {s.kind === 'files' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {FILES.map((f) => {
                    const file = files[f.key];
                    return (
                      <div key={f.key} data-field={f.key}>
                        <span className="ap-label">{f.label}</span>
                        <div className={`ap-drop${file ? ' has' : ''}`}
                          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(f.key, e.dataTransfer.files && e.dataTransfer.files[0]); }}>
                          <span style={{ color: file ? 'var(--success)' : 'var(--text-3)', display: 'inline-flex' }}><Icon name={file ? 'check' : 'upload'} size={18} stroke={2} /></span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {file
                              ? <><div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div><div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{sizeText(file.size)}</div></>
                              : <div style={{ fontSize: 14, color: 'var(--text-2)' }}>Drop the file here, or choose it</div>}
                          </div>
                          {file
                            ? <button type="button" className="ap-btn quiet" style={{ height: 34, fontSize: 13.5 }} onClick={() => setFiles((x) => ({ ...x, [f.key]: null }))}>Remove</button>
                            : <button type="button" className="ap-btn quiet" style={{ height: 34, fontSize: 13.5 }} onClick={() => pickers.current[f.key] && pickers.current[f.key].click()}>Choose a file</button>}
                          <input ref={(el) => { pickers.current[f.key] = el; }} type="file" accept={FILE_TYPES} hidden
                            onChange={(e) => { pick(f.key, e.target.files && e.target.files[0]); e.target.value = ''; }} />
                        </div>
                        {f.hint && <div className="ap-hint">{f.hint}</div>}
                        {fileErr[f.key] && <div className="ap-err" role="alert">{fileErr[f.key]}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          ))}
          {/* Bots fill in every field; people never see this one. */}
          <div className="ap-hp" aria-hidden="true"><label>Website URL<input ref={hp} tabIndex={-1} autoComplete="off" /></label></div>
          <div className="ap-card" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px', fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
              {Object.keys(errors).length
                ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{Object.keys(errors).length === 1 ? 'One answer needs' : `${Object.keys(errors).length} answers need`} a look above.</span>
                : 'E3 uses your answers only to consider your company for its work.'}
              {failed && <div className="ap-err" role="alert">{failed}</div>}
            </div>
            <button type="submit" className="ap-btn go" disabled={sending}>
              {sending ? 'Sending…' : <>Send application<Icon name="arrow-up" size={15} stroke={2.2} style={{ transform: 'rotate(90deg)' }} /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
